import { Fragment, InputHTMLAttributes, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { css, styled } from 'styled-components';
import {
	useController,
	UseControllerProps,
	Control,
	FieldValues,
	FieldPath,
} from 'react-hook-form';
import { Icon } from '@/core/components';

export interface InputFieldProps<TFieldValues extends FieldValues = FieldValues>
	extends Omit<InputHTMLAttributes<HTMLInputElement>, 'name'> {
	label?: string;
	dataQaId?: string;
	name: FieldPath<TFieldValues>;
	control: Control<TFieldValues>;
	rules?: UseControllerProps['rules'];
	// Optional visual status override (e.g., success or error borders/label)
	status?: 'default' | 'success' | 'error';
	// Optional end adornment (e.g., an icon button) rendered on the right inside the field wrapper
	endAdornment?: ReactNode;
}

const StyledInputWrapper = styled.div<{
	$isFocused: boolean;
	$hasValue: boolean;
	$hasError: boolean;
}>`
	width: 100%;
	height: 56px;
	min-height: 56px;
	margin: 8px 0;
	padding: 8px 16px;
	background: var(--Primary-white);
	overflow: hidden;
	border-radius: 12px;
	border: 1px solid
		${(p) => {
			if (p.$hasError) return css`var(--Red-80)`;
			return p.$isFocused ? css`var(--Primary-squla-blue)` : css`var(--Grey-40)`;
		}};
	justify-content: space-between;
	align-items: center;
	display: inline-flex;
	transition:
		padding 0.2s ease,
		outline 0.2s ease;
`;

const StyledInputContainer = styled.div`
	position: relative;
	width: 100%;
	height: 42px;
	min-height: 42px;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	display: inline-flex;
`;

const StyledLabel = styled.label<{
	$isFocused: boolean;
	$hasValue: boolean;
	$status: 'default' | 'success' | 'error';
}>`
	position: absolute;
	top: ${(p) => (p.$isFocused || p.$hasValue ? '0%' : '50%')};
	color: var(--Grey-80);
	font-size: ${(p) => (p.$isFocused || p.$hasValue ? '12px' : '16px')};
	line-height: ${(p) => (p.$isFocused || p.$hasValue ? '16px' : '24px')};
	word-wrap: break-word;
	transition:
		font-size 0.2s ease,
		line-height 0.2s ease,
		color 0.2s ease;
	transform: ${(p) => (p.$isFocused || p.$hasValue ? 'unset' : css`translateY(-50%)`)};
	pointer-events: auto;
`;

const StyledInput = styled.input<{ $isFocused: boolean; $hasValue: boolean }>`
	align-self: stretch;
	padding-left: 0;
	${(p) => (p.$isFocused || p.$hasValue ? 'padding-top: 18px;' : '')}
	flex: 1 1 0;
	width: 100%;
	border: none;
	outline: none;
	background: transparent;
	color: var(--Primary-squla-dark-blue);
	font-size: 16px;
	line-height: 24px;
	word-wrap: break-word;
	display: flex;
	justify-content: center;
	flex-direction: column;
	overflow: hidden;

	&:focus {
		outline: none;
	}
`;

const EndAdornment = styled.div`
	flex: 0 0 auto;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	margin-left: 8px;
	margin-right: 8px;
`;

const ErrorMessage = styled.div`
	color: var(--Red-80);
	font-size: 12px;
	display: flex;
	align-items: center;
	gap: 8px;
`;

export function InputField<TFieldValues extends FieldValues = FieldValues>(
	props: InputFieldProps<TFieldValues>,
) {
	const [isFocused, setIsFocused] = useState(false);
	const {
		label,
		dataQaId,
		control,
		name,
		children,
		onBlur,
		status = 'default',
		endAdornment,
		...restProps
	} = props;
	const { field, fieldState } = useController({ control, name });
	const hasValue = Boolean(field.value && String(field.value).length > 0);
	const inputId = useId();
	const errorId = `${inputId}-error`;
		

	let showError = false;
	showError = Boolean(fieldState.error);

	const ErrorElement = showError ? (
		<ErrorMessage
			id={errorId}
			role="alert"
		>
			<Icon name="alert-triangle" size={16} aria-hidden="true" />
			<span>{fieldState.error?.message}</span>
		</ErrorMessage>
	) : null;

	const hasError = Boolean(fieldState.error);
	return (
		<Fragment>
			<StyledInputWrapper
				data-qa-id={dataQaId}
				$isFocused={isFocused}
				$hasValue={hasValue}
				$hasError={hasError}
			>
				<StyledInputContainer>
					<StyledLabel
						htmlFor={inputId}
						$isFocused={isFocused}
						$hasValue={hasValue}
						$status={status}
					>
						{label}
					</StyledLabel>
					<StyledInput
						{...restProps}
						{...field}
						id={inputId}
						onFocus={() => {
							setIsFocused(true);
						}}
						onBlur={(e) => {
							setIsFocused(false);
							field.onBlur();
							if (onBlur) {
								onBlur(e);
							}
							field.onChange(field.value);
						}}
						aria-invalid={fieldState.invalid ? 'true' : 'false'}
						aria-describedby={fieldState.error ? errorId : undefined}
						$isFocused={isFocused}
						$hasValue={hasValue}
					/>
					{children}
				</StyledInputContainer>
				{endAdornment ? <EndAdornment>{endAdornment}</EndAdornment> : null}
			</StyledInputWrapper>
			{ErrorElement}
		</Fragment>
	);
}

export default InputField;